import {
    createContext,
    Dispatch,
    FC,
    PropsWithChildren,
    Reducer,
    useContext,
    useEffect,
    useReducer,
} from 'react';

import { useLocalStorage } from 'react-use';

import { CookieType } from '../entities/Cookie/Cookie';
import { defaultLocale, LocaleData } from '../entities/Locale/Locale';
import { useCookieSettings } from './CookieContext';

export const LocaleContext = createContext<LocaleData>(defaultLocale);
export const LocaleDispatchContext = createContext((localeData => localeData) as Dispatch<LocaleData>);

export const useLocale = () => useContext(LocaleContext);
export const useLocaleDispatch = () => useContext(LocaleDispatchContext);

const localeReducer: Reducer<LocaleData, LocaleData> = (prevState, localeData) => localeData;

export const LocaleProvider: FC<PropsWithChildren> = ({ children }) => {
    const cookieSettings = useCookieSettings();
    const functionalCookiesAccepted = cookieSettings[CookieType.functional];

    const [storedLocale, storeLocale] = useLocalStorage<LocaleData>('locale', undefined);

    const [locale, dispatch] = useReducer(localeReducer, storedLocale || defaultLocale);

    useEffect((): void => {
        const { language, direction } = locale;

        if (functionalCookiesAccepted) {
            storeLocale(locale);
        }

        document.documentElement.dir = direction;
        document.documentElement.lang = language;
    }, [locale, functionalCookiesAccepted]);

    return (
        <LocaleContext.Provider value={locale}>
            <LocaleDispatchContext.Provider value={dispatch}>
                {children}
            </LocaleDispatchContext.Provider>
        </LocaleContext.Provider>
    );
};
